import { Alert, Box, Card, Divider, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AdjustIcon from '@mui/icons-material/Adjust';
import React from 'react';
import { Translate } from 'react-localize-redux';

import { RequestStatusToChip } from './RequestTable';
import { requestTerminologyKey } from './requestModification';
import { RequestStatus, ServiceType } from './types';
import { statusToColor } from './utils';

export interface RequestManagementPanelProps {
    /** Picks the module's vocabulary for the items heading (tests / exams / surgical acts / items). */
    requestServiceType?: ServiceType;
    /** The per-item list. Always present: rows are read-only when the user may not modify them. */
    items: React.ReactNode;
    /** The single add control, or null when adding is not offered. */
    addItem: React.ReactNode;
    /** Shown in every state — the strip is how the panel says where the request stands. */
    currentStatus: RequestStatus;
    /** The step the existing workflow offers next, or null when it offers none. */
    nextAction: React.ReactNode;
    /** What recording the next action writes down; only shown alongside a next action. */
    nextActionNoteId?: string | null;
    /** Cancellation — destructive, so it lives at the bottom behind a separator. */
    cancel: React.ReactNode;
}

/**
 * SHER-909 / SHER-1062 — one card for everything that can be done to a request, laid out after
 * the reporter's mockup: the requested items with their row actions and one add button, a
 * status strip ("Statut actuel" + "Prochaine action"), a note saying what the next action
 * records, and "Annuler la demande" alone at the bottom.
 *
 * Composition over props-drilling, as in SHER-909. Each control already decides for itself
 * whether the user may see it (MODIFY_REQUESTS, CANCEL_REQUESTS, UPDATE_LAB_REQUESTS, plus the
 * server's own surgical transitions), and the next action is whichever of those existing
 * controls applies — this component has no workflow of its own to drift out of step with.
 *
 * Unlike SHER-909 the panel no longer disappears for a read-only user: the status is always
 * shown, so the page keeps the same shape whatever state the request is in.
 */
export const RequestManagementPanel: React.FC<RequestManagementPanelProps> = ({
    requestServiceType, items, addItem, currentStatus, nextAction, nextActionNoteId, cancel,
}) => {
    const statusColour = statusToColor(currentStatus);
    const hasNextAction = Boolean(nextAction);

    return (
        <Grid item xs={12}>
            <Card data-testid="request-management-panel" sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                        <Translate id="pages.hospital.services.request.management.title" />
                    </Typography>
                    <Divider sx={{ mt: '12px' }} />
                </Box>

                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1rem', mb: '8px' }}>
                        <Translate id={requestTerminologyKey(requestServiceType, 'items_section')} />
                    </Typography>
                    {items}
                    {addItem && <Box sx={{ mt: '16px' }}>{addItem}</Box>}
                </Box>

                <Box
                    data-testid="request-status-strip"
                    sx={{
                        display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '40px', rowGap: '12px',
                        px: '16px', py: '12px', borderRadius: '4px',
                        border: `1px solid ${alpha(statusColour, 0.3)}`,
                        backgroundColor: alpha(statusColour, 0.06),
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <AdjustIcon sx={{ color: statusColour }} />
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            <Translate id="pages.hospital.services.request.management.current_status" />
                        </Typography>
                        <RequestStatusToChip status={currentStatus} />
                    </Box>
                    {/* Only when the workflow offers a step: an empty "Prochaine action :" would
                        read as a broken button rather than as "nothing to do here".
                        No vertical rule between the halves (the mockup has one): a long status
                        label wraps the strip onto two lines, and the rule is then left dangling
                        at the end of the first. The gap separates them in both layouts. */}
                    {hasNextAction &&
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                <Translate id="pages.hospital.services.request.management.next_action" />
                            </Typography>
                            {nextAction}
                        </Box>
                    }
                </Box>

                {hasNextAction && nextActionNoteId &&
                    <Alert severity="info" variant="outlined" data-testid="request-next-action-note"
                        sx={{ backgroundColor: (theme) => alpha(theme.palette.info.main, 0.06) }}>
                        <Translate id={nextActionNoteId} />
                    </Alert>
                }

                {/* Cancel is the one irreversible control in the panel, so it is demoted: last,
                    and separated by a rule so it cannot be clicked on the way to something else. */}
                {cancel && <Divider sx={{ mx: '-24px' }} />}
                {cancel && <div data-testid="request-management-destructive">{cancel}</div>}
            </Card>
        </Grid>
    );
};

export default RequestManagementPanel;
